import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, useTheme, useMediaQuery, Container, Card, LinearProgress, Stepper, Step, StepLabel, Button, Snackbar, Alert } from '@mui/material';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { AlertInfo } from '../../components/Alerts/Alert';
import SimpleBackdrop from '../../components/Loading/SimpleBackdrop';

import AddressInfoStep from './CadEscriComponents/AddressInfoStep';
import PersonalInfoStep from './CadEscriComponents/PersonalInfoStep';
import ProfessionalInfoStep from './CadEscriComponents/ProfessionalInfoStep';
import ReviewStep from './CadEscriComponents/ReviewStep';
import RegistrationHeader from './CadEscriComponents/RegistrationHeader';
import { api } from '../../services/api';

export interface LawyerFormData {
    // Personal Information
    proprietario: string;
    email_escritorio: string;
    password: string;
    confirmPassword: string;
    telefone_escritorio: string;

    // Professional Information
    tipopessoa: string;
    cnpj_escritorio: string;
    nome_escritorio: string;
    razao_social_escritorio: string;

    // Address Information
    endereco_escritorio: string;
    numero_escritorio: string;
    bairro_escritorio: string;
    complemento_escritorio: string;
    uf_escritorio: string;
    cidade_escritorio: string;
    cep_escritorio: string;

    // Additional Information
    firm: string;
    website: string;
    bio: string;

    // Agreements
    agreeToTerms: boolean;
}



const steps = ['Dados Pessoais', 'Dados Profissionais', 'Endereço', 'Review'];

const initialValues: LawyerFormData = {
    // Personal Information
    proprietario: '',
    email_escritorio: '',
    password: '',
    confirmPassword: '',
    telefone_escritorio: '',

    // Professional Information
    tipopessoa: 'fisica',
    cnpj_escritorio: '',
    nome_escritorio: '',
    razao_social_escritorio: '',

    // Address Information
    endereco_escritorio: '',
    numero_escritorio: '',
    bairro_escritorio: '',
    complemento_escritorio: '',
    uf_escritorio: 'PA',
    cidade_escritorio: '',
    cep_escritorio: '',

    // Additional Information
    firm: '',
    website: '',
    bio: '',

    // Agreements
    agreeToTerms: false,
};

const RegistrationForm: React.FC = () => {
    const [activeStep, setActiveStep] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { plano = 'acesso' } = useParams();

    const validationSchema = [
        // Step 1: Personal Information
        Yup.object({
            proprietario: Yup.string().required('Nome completo é obrigatorio'),
            email_escritorio: Yup.string().email('Email Invalido').required('Email é obrigatorio'),
            password: Yup.string()
                .min(4, 'Senha muito curta')
                .required('Senha é obrigatorio'),
            confirmPassword: Yup.string()
                .oneOf([Yup.ref('password')], 'As senhas devem corresponder')
                .required('Por favor confirme sua senha'),
            telefone_escritorio: Yup.string()
                .matches(/^\(\d{2}\) \d{5}-\d{4}$|^\d{11}$/, 'Número de telefone não é válido')
                .required('O número de telefone é obrigatório'),
        }),

        // Step 2: Professional Information
        Yup.object({
            cnpj_escritorio: Yup.string().min(10, 'Dado invalido').required('Obrigatorio Preenchimento deste campo'),
            nome_escritorio: Yup.string().required('Nome Escritorio é obrigatorio'),
        }),

        // Step 3: Address Information
        Yup.object({
            cidade_escritorio: Yup.string().required('Cidade é obrigatório'),
            endereco_escritorio: Yup.string().required('Endereço é obrigatório'),
        }),

        // Step 4: Review and Agreement
        Yup.object({
            agreeToTerms: Yup.boolean()
                .oneOf([true], 'Você deve aceitar os termos e condições')
                .required('Você deve aceitar os termos e condições'),
        }),
    ];

    const formik = useFormik({
        initialValues,
        validationSchema: validationSchema[activeStep],
        onSubmit: async (values) => {
            if (activeStep < steps.length - 1) {
                if (activeStep === 0 && await checkEmailExists(values.email_escritorio)) return;
                if (activeStep === 1 && await checkCpfCnpjExists(values.cnpj_escritorio)) return;
                setActiveStep((prevStep) => prevStep + 1);
                return;
            }

            // Final submission
            setIsSubmitting(true);
            handleSubimitCad(values);
        },
    });

    async function checkEmailExists(email: string) {
        setIsSubmitting(true);
        const response = await api.post(`/checkemail`, { email });
        setIsSubmitting(false);
        if (response && response.data && response.data.success) {
            formik.setErrors({ email_escritorio: 'Este email já está cadastrado' });
            setSuccessMessage('Este email já está cadastrado');
            return true; // Email já existe
        }
        return false; // Email não existe
    }

    async function checkCpfCnpjExists(cpfcnpj: string) {
        setIsSubmitting(true);
        const response = await api.post(`/checkcpfcnpj`, { cpfcnpj });
        setIsSubmitting(false);
        if (response && response.data && response.data.success) {
            formik.setErrors({ cnpj_escritorio: 'Este Documento já está cadastrado no sistema' });
            setSuccessMessage('Este Documento já está cadastrado no sistema');
            return true; // Email já existe
        }
        return false; // Email não existe
    }

    async function handleSubimitCad(formValues: LawyerFormData) {
        try {
            const {
                nome_escritorio, razao_social_escritorio, cnpj_escritorio,
                endereco_escritorio, numero_escritorio, bairro_escritorio, complemento_escritorio,
                uf_escritorio, cidade_escritorio, cep_escritorio,
                telefone_escritorio, email_escritorio, proprietario, password, tipopessoa
            } = formValues;
            const dataEscritorio = {
                nome_escritorio, razao_social_escritorio, cnpj_escritorio,
                endereco_escritorio, numero_escritorio, bairro_escritorio, complemento_escritorio,
                uf_escritorio, cidade_escritorio, cep_escritorio,
                telefone_escritorio, email_escritorio, proprietario, password, tipopessoa,
                isTesting: plano === 'acesso' ? true : false
            };
            const responseEsc = await api.post(`/createescritorio`, dataEscritorio);
            if (responseEsc && responseEsc.status === 200 && responseEsc.data) {
                const login = await api.post(`/login`, { user: email_escritorio, pass: password });
                if (login && login.status === 200 && login.data) {
                    if (login.data.id_usuario) {
                        sessionStorage.setItem("infoLogin", JSON.stringify(login.data));
                    }
                }
                AlertInfo({ icon: 'success', title: 'CADASTRO CONCLUIDO' }).then(() => {
                    const channel = plano !== 'acesso' ? `/pagamento/${plano}` : '/congratulations';
                    navigate(channel);
                });
            }
            setIsSubmitting(false);


        } catch (error) {
            console.log(error);
            setIsSubmitting(false);
            AlertInfo({ icon: 'error', title: 'Falha na Conexão' });
        }
    }

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    const handleCloseSnackbar = () => {
        setSuccessMessage('');
    };

    const getStepContent = (step: number) => {
        switch (step) {
            case 0:
                return <PersonalInfoStep formik={formik} activeStep={activeStep} />;
            case 1:
                return <ProfessionalInfoStep formik={formik} activeStep={activeStep} />;
            case 2:
                return <AddressInfoStep formik={formik} activeStep={activeStep} />;
            case 3:
                return <ReviewStep formik={formik} activeStep={activeStep} />;
            default:
                return 'Unknown step';
        }
    };

    return (
        <Card sx={{
            maxWidth: 900,
            mx: 'auto',
            borderRadius: 3,
            overflow: 'visible',
            boxShadow: '0 8px 40px rgba(0, 0, 0, 0.12)'
        }}>
            <SimpleBackdrop open={isSubmitting} />
            {isSubmitting && (
                <LinearProgress
                    sx={{
                        borderTopLeftRadius: 12,
                        borderTopRightRadius: 12,
                        height: 6
                    }}
                />
            )}

            <Box sx={{ px: { xs: 2, sm: 4 }, py: 4 }}>
                <Stepper
                    activeStep={activeStep}
                    alternativeLabel={!isMobile}
                    orientation={isMobile ? 'vertical' : 'horizontal'}
                    sx={{ mb: 4 }}
                >
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <form onSubmit={formik.handleSubmit}>
                    {getStepContent(activeStep)}

                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mt: 5,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Button
                            disabled={activeStep === 0}
                            onClick={handleBack}
                            startIcon={<ChevronLeft size={18} />}
                            sx={{ mr: 1 }}
                        >
                            VOLTAR
                        </Button>

                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={isSubmitting || (activeStep === steps.length - 1 && !formik.values.agreeToTerms)}
                            endIcon={activeStep === steps.length - 1 ? <Check size={18} /> : <ChevronRight size={18} />}
                            sx={{
                                minWidth: 120,
                                transition: 'all 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                }
                            }}
                        >
                            {activeStep === steps.length - 1 ? 'ENVIAR' : 'PROXIMO'}
                        </Button>
                    </Box>
                </form>
            </Box>

            <Snackbar
                open={!!successMessage}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity="error"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {successMessage}
                </Alert>
            </Snackbar>
        </Card>
    );
};

export default function CadastroEscritorioAdvogado() {
    const { plano = 'acesso' } = useParams();

    return (
        <Container maxWidth="lg">
            <RegistrationHeader isText={plano === 'acesso' ? true : false} />
            <RegistrationForm />
        </Container>
    );
}